import type { Extent } from "ol/extent";
import { getBottomLeft, getTopRight } from "ol/extent";

import type { SearchSitesParams } from "../../../../api/site/search";
import { AbstractPanel } from "../../../../common/abstractconditionpanel";
import { allObsNetworkIds, InstallationSituationId, installationSituationIdToString, obsNetworkIdToString } from "../../../../enums";
import hx from "../../../../utils/hydroxie";
import { pickElement } from "../../../../utils/pickelements";

/**
 * 観測点検索→検索条件エリア→基本情報パネル
 */
export class BasicPanel extends AbstractPanel<SearchSitesParams> {
    /**
     * 初期化処理を行う。
     * 観測点の基本情報検索条件UI要素を生成し、イベントリスナーを設定する。
     */
    async init() {
        const elem = this.getElem();

        elem.replaceChildren(...this.#createElement());

        elem.addEventListener("change", () => {
            const params = this.#getParams();
            this.emit("update", params);
        });
    }

    #createElement() {
        const targetInstallationSituationIds = [
            InstallationSituationId.Ground,
            InstallationSituationId.Borehole,
        ];

        return [
            hx("div", { className: "field field-site-code" },
                hx("label", { className: "label" }, "観測点コード"),
                hx("div", { className: "control horizontal" },
                    hx("input", { type: "text", className: "input is-small", name: "siteCode" }))),

            hx("div", { className: "field field-site-name" },
                hx("label", { className: "label" }, "観測点名"),
                hx("div", { className: "control horizontal" },
                    hx("input", { type: "text", className: "input is-small", name: "siteName" }))),

            hx("div", { className: "field" },
                hx("label", { className: "label" }, "緯度"),
                hx("div", { className: "control horizontal" },
                    hx("input", { type: "number", step: "any", min: "-90", max: "90", className: "input is-small", name: "latFrom" }),
                    hx("span", "〜"),
                    hx("input", { type: "number", step: "any", min: "-90", max: "90", className: "input is-small", name: "latTo" }))),

            hx("div", { className: "field" },
                hx("label", { className: "label" }, "経度"),
                hx("div", { className: "control horizontal" },
                    hx("input", { type: "number", step: "any", min: "-180", max: "180", className: "input is-small", name: "lonFrom" }),
                    hx("span", "〜"),
                    hx("input", { type: "number", step: "any", min: "-180", max: "180", className: "input is-small", name: "lonTo" }))),

            hx("div", { className: "field field-elevation" },
                hx("label", { className: "label" }, "標高 [m]"),
                hx("div", { className: "control horizontal" },
                    hx("input", { type: "number", step: "1", className: "input is-small", name: "elevationFrom" }),
                    hx("span", "〜"),
                    hx("input", { type: "number", step: "1", className: "input is-small", name: "elevationTo" }))),

            hx("div", { className: "field field-obs-network" },
                hx("label", { className: "label" }, "観測網"),
                hx("div", { className: "control horizontal" },
                    allObsNetworkIds
                        .map((x) => {
                            return hx("label", { className: "label" },
                                hx("input", { type: "checkbox", id: `obsNetwork${x}`, value: "" + x, checked: true }),
                                obsNetworkIdToString(x));
                        }))),

            hx("div", { className: "field field-installation-situation" },
                hx("label", { className: "label" }, "設置場所"),
                hx("div", { className: "control horizontal" },
                    targetInstallationSituationIds
                        .map((x) => {
                            return hx("label", { className: "label" },
                                hx("input", { type: "checkbox", id: `installationSituationId${x}`, value: "" + x, checked: true }),
                                installationSituationIdToString(x));
                        }))),

        ];
    }

    #getParams() {
        let siteCode: string | undefined = this.getString("[name='siteCode']");

        if (!siteCode) {
            siteCode = undefined;
        }

        let siteName: string | undefined = this.getString("[name='siteName']");

        if (!siteName) {
            siteName = undefined;
        }

        const latFrom = this.getLat("[name='latFrom']");
        const latTo = this.getLat("[name='latTo']");

        const lonFrom = this.getLon("[name='lonFrom']");
        const lonTo = this.getLon("[name='lonTo']");

        const elevationFrom = this.getNumber("[name='elevationFrom']");
        const elevationTo = this.getNumber("[name='elevationTo']");

        const obsNetworkId = this.getChecked("[id^='obsNetwork']:checked", Number);
        const installationSituationId = this.getChecked("[id^='installationSituationId']:checked", Number);

        return {
            siteCode,
            siteName,
            latFrom,
            latTo,
            lonFrom,
            lonTo,
            elevationFrom,
            elevationTo,
            obsNetworkId,
            installationSituationId,
        };
    }

    /**
     * 地図上で選択された範囲を緯度経度の検索条件に設定する。
     *
     * @param extent 選択された範囲（Extent）
     */
    setExtent(extent: Extent) {
        const elem = this.getElem();
        const bottomLeft = getBottomLeft(extent);
        const topRight = getTopRight(extent);

        const lonFrom = pickElement<HTMLInputElement>("[name='lonFrom']", elem);
        const latFrom = pickElement<HTMLInputElement>("[name='latFrom']", elem);
        const lonTo = pickElement<HTMLInputElement>("[name='lonTo']", elem);
        const latTo = pickElement<HTMLInputElement>("[name='latTo']", elem);

        lonFrom.value = bottomLeft[0].toFixed(4);
        latFrom.value = bottomLeft[1].toFixed(4);
        lonTo.value = topRight[0].toFixed(4);
        latTo.value = topRight[1].toFixed(4);

        lonFrom.dispatchEvent(new Event("change", { bubbles: true }));
    }
}
